'use client'

import AppIcon from "@/app/components/atomic/AppIcon"
import { useTracking } from '@/app/contexts/tracking-context'
import { ReactNode, useState } from 'react'

interface AnimatedTabsProps {
    activeTab: string
    commentCount: number
    expertContent: ReactNode
    questionsContent: ReactNode
    bottomSections: ReactNode
    showCommentForm?: boolean
}

export default function AnimatedTabs({ 
    activeTab: initialActiveTab,
    commentCount,
    expertContent, 
    questionsContent,
    bottomSections,
    showCommentForm = true
}: AnimatedTabsProps) {
    const [activeTab, setActiveTab] = useState(initialActiveTab)
    const { track } = useTracking();

    const handleTabChange = (tab: string) => {
        setActiveTab(tab)

        if (tab === 'questions') {
            track({ event: 'affichage_commentaire' })
        }
    }

    return (
        <>
            <div className="w-full flex flex-col-reverse">
                {/* Tab Content with Fade Animation */}
                <div className="relative mt-rm-35">
                    {/* Expert Content */}
                    <div className={`w-full transition-opacity duration-300 ease-in-out ${
                        activeTab === 'article' ? 'opacity-100' : 'hidden'
                    }`}>
                        {expertContent}

                        { showCommentForm &&
                            <div className="flex sm:items-center justify-between flex-col sm:flex-row gap-rm-1 border border-blue-200 rounded-rm-10 p-rm-20 mt-rm-25 sm:mt-0">
                                <div className="flex items-center gap-rm-10">
                                    <div className="w-[50px] h-[50px] bg-orange-100 rounded-rm-10 flex items-center justify-center">
                                        <AppIcon iconClassName="fa-comment" type="regular" className="text-orange-700 text-rm-18" />
                                    </div>
                                    <label
                                        htmlFor="message"
                                        className="flex flex-col justify-center items-start text-rm-16 text-blue-900"
                                    >
                                        <span className="font-bold">
                                            Posez une question
                                        </span>
                                        <span>
                                            Un expert vous répondra
                                        </span>
                                    </label>
                                </div>

                                <button
                                    className="flex w-full sm:max-w-[300px] justify-center items-center gap-2 px-5 py-4 rounded-lg md:rounded font-bold text-white text-sm bg-orange-700
                                    transition-all duration-300 whitespace-nowrap
                                    hover:shadow-outline hover:shadow-orange-500"
                                    onClick={() => {
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                        handleTabChange('questions');
                                    }}
                                >
                                    VOIR LES QUESTIONS
                                </button>
                            </div>
                        }
                    </div>
                    
                    {/* Questions Content */}
                    <div className={`w-full transition-opacity duration-300 ease-in-out ${
                        activeTab === 'questions' ? 'opacity-100' : 'hidden'
                    }`}>
                        {questionsContent}
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="w-full flex relative bg-white shadow-md shadow-blue-200 my-2">
                    <span
                        onClick={() => handleTabChange('article')}
                        className={`border-b w-1/2 text-rm-14 lg:text-rm-16 select-none cursor-pointer pb-2 transition-all 
                        duration-300 flex items-center justify-center ${
                            activeTab === 'article' 
                                ? 'border-b-2 border-blue-600 text-blue-600 font-semibold' 
                                : 'border-blue-300 text-blue-900/70 hover:text-blue-900/90'
                        }`}
                    >
                        <AppIcon
                            iconClassName="fa-newspaper"
                            type={'regular'}
                            className={`mr-2 ${activeTab === 'article' ? 'text-blue-600' : 'text-blue-900/70'} hidden sm:inline-block`}
                        />
                        Notre article
                    </span>
                    <div
                        onClick={() => handleTabChange('questions')}
                        className={`border-b w-1/2 select-none cursor-pointer transition-all pb-2
                        text-center duration-300 group inline-flex items-center justify-center ${
                            activeTab === 'questions' 
                                ? 'border-b-2 border-blue-600' 
                                : 'border-blue-300'
                        }`}
                    >
                        <h2
                            className={`text-rm-14 lg:text-rm-16 mb-0 ${
                                activeTab === 'questions' 
                                    ? 'text-blue-600 font-semibold' 
                                    : 'text-blue-900/70 hover:text-blue-900/90'
                            }`}
                        >
                            <AppIcon
                                iconClassName="fa-comments"
                                type={'regular'}
                                className={`mr-2 ${activeTab === 'questions' ? 'text-blue-600' : 'text-blue-900/70'} hidden sm:inline-block`}
                            />

                            Vos questions
                        
                        </h2>
                        <span
                            className={`ml-2 px-4 rounded-[100px] text-white text-rm-14
                             group-hover:bg-green-700
                            ${activeTab === 'questions' ? 'bg-green-700' : 'bg-green-700/70'}`}
                        >
                            {commentCount}
                        </span>
                    </div>
                
                </div>
            </div>

            {/* Bottom Sections - Only show for expert tab */}
            <div className={`transition-opacity duration-500 ${
                activeTab === 'article' ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}>
                {activeTab === 'article' && bottomSections}
            </div>
        </>
    )
} 